import { Contents, Rendition, Location, EpubCFI } from 'epubjs';
import { nextTick } from 'vue';
import Section from 'epubjs/types/section';
import { AdditionalStylesheets } from '../../types';
import useRenditionStore from '../../stores/rendition';
import useContent from '../../composables/useContent';
import useAudioBooksView from './use-audio-books-view';
import useRenderer from '../use-renderer';
import useReaderEngine from '../../engine/use-reader-engine';
import useClickHandler from '../../composables/use-click-handler';
import useAudioTracks from '../../composables/use-audio-tracks';
import useSettingStore from '../../stores/setting';
import useMediaStore from '../../stores/media';
import useAudioAnnotations from '../../composables/use-audio-annotations';
import { storeToRefs } from 'pinia';

interface ContinuousReaderCallbacks {
  onRelocated: (location: Location, rendition: Rendition) => Promise<void>;
}

const createContentsChangeHandler =
  (_rendition: Rendition, stylesheets: AdditionalStylesheets[] | undefined) => async (contents: Contents) => {
    const { updateLayoutForPagesLinks, colouriseByPage } = useAudioBooksView();
    const { getPageSpineItem } = useReaderEngine();
    const { addStylesheetCss, applyThemeAndBackground } = useRenderer();
    const { loadTracks } = useAudioTracks();
    const clickHandler = useClickHandler();
    // Add visual annotations for audio highlighting
    const { isTextClickPlaybackEnabled } = storeToRefs(useSettingStore());
    const { isPlayingMainLanguage, currentPageTracks } = storeToRefs(useMediaStore());
    const { setCurrentTrack, setIsUserChangingTrack } = useMediaStore();
    const { addTrackClickHandlers } = useAudioAnnotations();

    // Apply unified theme and background FIRST (sets base background color for all continuous books)
    applyThemeAndBackground(contents, 'continuous');

    // Then apply continuous-specific enhancements
    updateLayoutForPagesLinks(contents);
    colouriseByPage(contents); // This adds alternating page colors for reflowable books
    addStylesheetCss(contents, stylesheets);

    // Register click listener for this iframe FIRST, before adding elements
    clickHandler.registerContents(contents);

    const spineItem = getPageSpineItem(contents.sectionIndex);
    await loadTracks(spineItem);

    if (isPlayingMainLanguage.value && isTextClickPlaybackEnabled.value) {
      const spineIndex = contents.sectionIndex;

      // Add handlers immediately - hooks.content fires when iframe is ready
      if (!contents.document || !contents.document.body) {
        return;
      }

      // Filter to tracks that belong to THIS specific iframe by matching spinePos
      const tracksForThisSpine = currentPageTracks.value.filter((track) => {
        if (!track.cfi) return false;
        const parsedCfi = new EpubCFI(track.cfi);
        return parsedCfi.spinePos === spineIndex;
      });

      // Only add handlers if this spine actually has tracks
      if (tracksForThisSpine.length > 0) {
        addTrackClickHandlers(
          tracksForThisSpine,
          (clickedTrack) => {
            setCurrentTrack(clickedTrack);
            setIsUserChangingTrack(true);
          },
          spineIndex,
          contents.document,
        );
      }
    }
  };

const addClickListenerToIframe = (contents: Contents) => {
  const { addEventToIframeContents } = useRenderer();
  addEventToIframeContents(contents, 'click', (event: MouseEvent) => {
    window.parent.postMessage({ type: 'iframe-click' }, '*');
  });
};

const addKeyboardNavigationToIframe = (contents: Contents) => {
  const { addEventToIframeContents } = useRenderer();
  addEventToIframeContents(contents, 'keydown', (event: KeyboardEvent) => {
    // eslint-disable-next-line default-case
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        window.parent.postMessage({ type: 'keyboard-navigation', direction: 'next' }, '*');
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        window.parent.postMessage({ type: 'keyboard-navigation', direction: 'previous' }, '*');
        break;
    }

    if (event.altKey && (event.key === 'ArrowRight' || event.key === 'ArrowDown')) {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-navigation', direction: 'next' }, '*');
    } else if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowUp')) {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-navigation', direction: 'previous' }, '*');
    }

    if (event.code === 'Space' && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-player' }, '*');
    } else if (event.shiftKey && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-player' }, '*');
    } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'i') {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-toc' }, '*');
    } else if (event.key.toLowerCase() === 'm' && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-mute' }, '*');
    } else if (event.key === 'Escape') {
      event.preventDefault();
      window.parent.postMessage({ type: 'keyboard-shortcut', action: 'close-overlays' }, '*');
    }
  });
};

const setupAudioTrackClickHandlers = (rendition: Rendition) => {
  const mediaStore = useMediaStore();
  const { isTextClickPlaybackEnabled } = storeToRefs(useSettingStore());

  rendition.on('markClicked', (cfiRange: string, data: any) => {
    if (data?.trackId && isTextClickPlaybackEnabled.value) {
      const { currentPageTracks } = storeToRefs(mediaStore);
      const track = currentPageTracks.value.find((t) => t.text.srcFragmentId === data.trackId);

      if (track) {
        mediaStore.setCurrentTrack(track);
        mediaStore.playing = true;
        mediaStore.setIsUserChangingTrack(true);
      }
    }
  });
};

const useContinuousReader = () => {
  const renditionStore = useRenditionStore();
  const { setFontSize } = renditionStore;
  const settingStore = useSettingStore();
  const content = useContent();
  const { getCurrentTarget } = useRenditionStore();

  const setup = (rendition: Rendition, stylesheets: AdditionalStylesheets[], callbacks: ContinuousReaderCallbacks) => {
    // Clear the state on each book open.
    renditionStore.setRendition(rendition);
    content.handleBookLocations();

    setFontSize(settingStore.fontSize);

    // TODO - move this to rendition store
    if (rendition?.themes) {
      rendition.themes.font('Proxima Nova');
    }

    const contentChangeHandler = createContentsChangeHandler(rendition, stylesheets);
    rendition.hooks.content.register((contents: Contents) => {
      contentChangeHandler(contents);
    });

    const getTarget = (section: Section): string | undefined => {
      return getCurrentTarget();
    };

    const getOffset = (section: Section, target: string | undefined): { top: number; left: number } => {
      let offset = { top: 0, left: 0 };
      const view = (rendition as any).manager.views.find(section);

      if (view && target) {
        offset = view.offset();
        // Because we didn't destroy the target above, this now receives a valid CFI
        const elementOffset = view.locationOf(target);

        if (elementOffset && elementOffset.top) {
          offset.top += elementOffset.top;
        }
      }
      return offset;
    };
    const scrollOnNavigation = async (section: Section) => {
      await nextTick(() => {
        const target = getTarget(section);
        const offset = getOffset(section, target);
        if ((rendition as any).manager.scrollTop !== offset.top) {
          (rendition as any).manager.scrollTo(offset.left, offset.top);
        }
      });
    };
    const scrollOnDisplayed = async (section: Section) => {
      if (section.index === 0) {
        /**
         * First time load case.
         */
        return;
      }
      /**
       * Book is loaded inside of container "epub-container" with "overflow: hidden scroll;" css rule.
       * So during book load, each page is loaded inside of "epub-container" and you can navigate through pages by
       * scrolling.
       * Also, we aren't loading the whole book content. But only parts of it and only on some user actions.
       * If to go to specific position or page(like from TOC) inside a book, epubJs started to load pages both before and after
       * current location. If content is loaded before current location, this content will push content below itself.
       * This is incorrect, as it's expected to see the content you are originally scrolled to.
       * To fix this issue, we additionally scroll to the target content after content in queue being rendered.
       */
      await nextTick(() => {
        const target = getTarget(section);
        if (target) {
          const offset = getOffset(section, target);
          if ((rendition as any).manager.scrollTop !== offset.top) {
            /**
             * Handle future cases, like TOC navigation or Bookmarks.
             */
            rendition.on('displayed', scrollOnNavigation);
            rendition.off('displayed', scrollOnDisplayed);
            /**
             * Fix of iOS bug with incorrect positioning while navigating using TOC or so.
             * So in some cases IFrame content has not been fully rendered, and offset is calculated in correctly.
             * To fix that, we are waiting for the next tick and re-displaying same target.
             * In case if re-displaying of target we are fixing ePubJS lib bug that prevents scrolling to correct
             * position, when requested view was not rendered.
             */
            if (rendition?.book?.spine?.get(target)) {
              // fire-and-forget re-display ; race with forceReRendering → catch swallows hooks.content destruction errors
              rendition.display(target).catch(() => {});
            }
          }
        }
      });
    };

    rendition.on('relocated', async (location: Location) => {
      await callbacks.onRelocated(location, rendition);
    });

    rendition.on('displayed', scrollOnDisplayed);

    rendition.hooks.content.register((contents: Contents) => addClickListenerToIframe(contents));
    rendition.hooks.content.register((contents: Contents) => addKeyboardNavigationToIframe(contents));

    setupAudioTrackClickHandlers(rendition);
  };

  return { setup };
};

export default useContinuousReader;
